import React from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SaudiRiyalIcon } from '../../ui';
import SectionCard from './SectionCard';
import KpiStat from './KpiStat';
import StatusBar, { statusBarItems } from './StatusBar';
import useModuleStats from './useModuleStats';
import RecentItems from './RecentItems';
import { proposalStatusLabels, formatMoney } from './statusLabels';
import { formatCount } from '../overview/format';

/** Proposals — pipeline distribution + real money KPIs (SAR). */
const ProposalsSection: React.FC = () => {
  const { isRTL } = useLanguage();
  const { data, loading, error } = useModuleStats<any>('/proposals/statistics/');

  if (error) return null;
  // No records yet: keep the module visible with an empty state rather than hiding it.
  const empty = !loading && (!data || (data.total ?? 0) === 0)
    ? {
        title: isRTL ? 'لا توجد عروض حتى الآن' : 'No proposals yet',
        text: isRTL ? 'ستظهر هنا قيمة العروض ومسارها بمجرد إنشاء أول عرض.' : 'Proposal value and pipeline will appear here once the first proposal is created.',
        action: { to: '/proposals/new', label: isRTL ? 'إنشاء عرض' : 'Create proposal' },
      }
    : null;

  const pipeline: Record<string, number> = {
    draft: data?.draft,
    pending_review: data?.pending_review,
    approved: data?.approved,
    sent: data?.sent,
    accepted: data?.accepted,
    rejected: data?.rejected,
    expired: data?.expired,
  };

  const decided = (data?.accepted ?? 0) + (data?.rejected ?? 0);
  const acceptanceRate = decided > 0 ? Math.round(((data?.accepted ?? 0) / decided) * 100) : null;

  const money = (v: any) => (
    <span className="inline-flex items-center gap-1">
      {formatMoney(v ?? 0, isRTL)}
      <SaudiRiyalIcon className="w-4 h-4 opacity-70" />
    </span>
  );

  return (
    <SectionCard
      icon="bi-file-earmark-text"
      title={isRTL ? 'العروض' : 'Proposals'}
      caption={isRTL ? 'مسار العروض وقيمتها' : 'Pipeline and value'}
      to="/proposals"
      loading={loading}
      empty={empty}
      stats={(
        <>
          <KpiStat value={formatCount(data?.total)} label={isRTL ? 'الإجمالي' : 'Total'} />
          {/* Money counted only for proposals that reached the client —
              drafts are internal and would inflate the number. */}
          <KpiStat value={money(data?.sent_value)} label={isRTL ? 'قيمة العروض المرسلة' : 'Sent Value'} />
          <KpiStat value={money(data?.accepted_value)} label={isRTL ? 'قيمة المقبولة' : 'Accepted Value'} tone="success" />
          {acceptanceRate !== null ? (
            <KpiStat value={`${acceptanceRate}%`} label={isRTL ? 'معدل القبول' : 'Acceptance Rate'} tone="primary" />
          ) : (
            <KpiStat value={formatCount(data?.draft)} label={isRTL ? 'مسودات' : 'Drafts'} />
          )}
        </>
      )}
    >
      <StatusBar items={statusBarItems(pipeline, proposalStatusLabels(isRTL))} />
      <RecentItems
        endpoint="/proposals/"
        title={isRTL ? 'أحدث العروض' : 'Recent Proposals'}
        itemLink={(pr) => `/proposals/${pr.id}`}
        primary={(pr) => (isRTL && pr.title_ar ? pr.title_ar : pr.title)}
        secondary={(pr) => pr.client_name || pr.proposal_id}
        statusKey={(pr) => pr.status}
        labels={proposalStatusLabels(isRTL)}
      />
    </SectionCard>
  );
};

export default ProposalsSection;
